import { Icon, MenuItem } from "@material-ui/core";
import React, { Component, Fragment } from "react";
import styled from "styled-components";

import { ThemeType } from "common/constants";
import { ActionPermissionsObject, PermissionActionType, WorkspaceResourceType } from "common/interfaces";
import getObjectHash from "common/utils/getObjectHash";
import UpgradeIcon from "js/Components/UpgradeIcon";
import ThemesPaneController from "js/controllers/ThemesPaneController";
import WorkspaceController from "js/controllers/WorkspaceController";
import { ds } from "js/core/models/dataService";
import themeManager, { SHARED_THEME_SORT } from "js/core/themeManager";
import { ThemeThumbnail } from "js/editor/ThemeEditor/components/ThemeThumbnail";
import { t } from "js/i18n";
import { app } from "js/namespaces";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { Gap10, Gap30 } from "js/react/components/Gap";
import { ScrollBox } from "js/react/components/LayoutGrid";
import { SimpleLabel } from "js/react/components/SimpleLabel";
import { IconMenu } from "js/react/components/UiComponents";
import { _ } from "js/vendor";

const ThumbnailScrollBox = styled(ScrollBox)`
    padding: 0 20px;
    background: #f1f1f1;
    scrollbar-gutter: stable;
`;

export const ThemeThumbnailList = ThemesPaneController.withState(class ThemeThumbnailList extends Component {
    constructor() {
        super();

        this.state = {
            userThemes: [],
            sharedThemes: [],
            resources: [],
        };

        this.addThemePromises = {};
    }

    componentDidMount() {
        ThemesPaneController.initialize({
            resourceTypes: [WorkspaceResourceType.THEME],
            workspaceId: WorkspaceController.workspaceId,
            sort: [...SHARED_THEME_SORT],
            filters: [{
                field: "resource.metadata.isPublished",
                operator: "eq",
                value: true
            }]
        });
    }

    componentDidUpdate(prevProps, prevState) {
        const { data, initialized } = this.props;

        if (!initialized) {
            return;
        }

        // Load more themes until we have them all, this component should be refactored to do proper pagination
        if ((prevProps.initialized !== initialized || prevProps.data.length !== data.length) && ThemesPaneController.canLoadMore) {
            ThemesPaneController.loadMore();
        }

        if (prevProps.initialized !== initialized || getObjectHash(data) !== getObjectHash(prevProps.data)) {
            for (const { resource } of data) {
                if (resource.metadata.isShared) {
                    this.addSharedTheme(resource);
                } else {
                    this.addUserTheme(resource);
                }
            }
        }
    }

    async addSharedTheme(resource) {
        const id = resource.resourceId;
        if (!this.addThemePromises[id]) {
            this.addThemePromises[id] = (async () => {
                const theme = await themeManager.getSharedTheme(id, true);
                this.setState(state => ({
                    ...state,
                    sharedThemes: [...state.sharedThemes, theme],
                    resources: [...state.resources, resource],
                }));
            })();
        }

        return this.addThemePromises[id];
    }

    async addUserTheme(resource) {
        const id = resource.resourceId;
        if (!this.addThemePromises[id]) {
            this.addThemePromises[id] = (async () => {
                const theme = await themeManager.getUserTheme(id, true);
                this.setState(state => ({
                    ...state,
                    userThemes: [...state.userThemes, theme],
                    resources: [...state.resources, resource],
                }));
            })();
        }

        return this.addThemePromises[id];
    }

    render() {
        const {
            initialized,
            fetching,
            styles,
            selectedTheme,
            requireTeamTheme,
            onClick,
            onDoubleClick,
            pptTheme,
            width,
        } = this.props;
        const {
            userThemes,
            sharedThemes,
            resources,
        } = this.state;

        if (!initialized) {
            return (
                <ThumbnailScrollBox id="theme-thumbnail-list" fillHeight fillWidth style={styles}>
                    <FetchingClickShield visible backgroundColor="white" />
                </ThumbnailScrollBox>
            );
        }

        const builtInThemes = ds.builtInThemes.models;

        const canUpgradeToUseTeamThemes = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.USE);

        return (
            <ThumbnailScrollBox id="theme-thumbnail-list" fillHeight fillWidth style={styles}>
                <FetchingClickShield visible={fetching} backgroundColor="white" />
                {!fetching && <>
                    {pptTheme && (
                        <Fragment>
                            <SimpleLabel>{t("POWERPOINT THEME")}</SimpleLabel>
                            <Gap10 />
                            <ThemeThumbnailGrid
                                type={ThemeType.PPT_THEME}
                                themes={[pptTheme]}
                                resources={resources}
                                selectedTheme={selectedTheme}
                                onClick={theme => onClick(theme, ThemeType.TEAM)}
                                onDoubleClick={theme => onDoubleClick(theme, ThemeType.TEAM)}
                                width={width}
                            />
                            <Gap30 />
                        </Fragment>
                    )}
                    {sharedThemes.length > 0 && (
                        <Fragment>
                            <SimpleLabel>
                                {t("TEAM THEMES")}
                                {canUpgradeToUseTeamThemes && (
                                    <UpgradeIcon style={{ marginLeft: 10 }} />
                                )}
                            </SimpleLabel>
                            <Gap10 />
                            <ThemeThumbnailGrid
                                type={ThemeType.TEAM}
                                themes={sharedThemes}
                                resources={resources}
                                selectedTheme={selectedTheme}
                                onClick={theme => onClick(theme, ThemeType.TEAM)}
                                onDoubleClick={theme => onDoubleClick(theme, ThemeType.TEAM)}
                                disabled={canUpgradeToUseTeamThemes}
                                width={width}
                            />
                            <Gap30 />
                        </Fragment>
                    )}
                    {!requireTeamTheme && userThemes.length > 0 && (
                        <Fragment>
                            <SimpleLabel>
                                {t("SAVED THEMES")}
                            </SimpleLabel>
                            <Gap10 />
                            <ThemeThumbnailGrid
                                type={ThemeType.USER}
                                themes={userThemes}
                                resources={resources}
                                selectedTheme={selectedTheme}
                                onClick={theme => onClick(theme, ThemeType.USER)}
                                onDoubleClick={theme => onDoubleClick(theme, ThemeType.USER)}
                                width={width}
                            />
                            <Gap30 />
                        </Fragment>
                    )}
                    {!requireTeamTheme && (
                        <Fragment>
                            <SimpleLabel>{t("BEAUTIFUL.AI THEMES")}</SimpleLabel>
                            <Gap10 />
                            <ThemeThumbnailGrid
                                type={ThemeType.BUILT_IN}
                                themes={builtInThemes}
                                resources={resources}
                                selectedTheme={selectedTheme}
                                onClick={theme => onClick(theme, ThemeType.BUILT_IN)}
                                onDoubleClick={theme => onDoubleClick(theme, ThemeType.BUILT_IN)}
                                width={width}
                            />
                        </Fragment>
                    )}
                </>}
            </ThumbnailScrollBox>
        );
    }
});

const ThemeThumbnailGridContainer = styled.div`
    position: relative;
    width: 100%;
    transition: 500ms;
`;

const ThemeThumbnailWrapper = styled.div.attrs(props => ({
    style: {
        ...props.bounds
    }
}))`
    position: absolute;
    transition: ${props => props.transition ? 500 : 0}ms;
    width: 200px;
    height: 180px;
`;

class ThemeThumbnailGrid extends Component {
    constructor() {
        super();
        this.ref = React.createRef();

        this.state = {
            width: null,
            transforms: null,
            transition: false
        };

        this.isUnmounted = false;
        this.layoutDebounced = _.debounce(this.layout, 100);
    }

    componentDidMount() {
        this.setState({ transition: true });

        this.observer = new ResizeObserver(entries => {
            const entry = entries[0];
            if (!entry) {
                return;
            }

            const width = entry.contentRect.width;
            if (this.state.width !== width) {
                this.layoutDebounced(entry.contentRect.width);
            }
        });

        this.observer.observe(this.ref.current);
    }

    componentWillUnmount() {
        this.isUnmounted = true;

        this.observer.disconnect();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        const { themes } = this.props;

        if (prevProps.themes.length !== themes.length && this.ref.current.clientWidth) {
            this.layout(this.ref.current.clientWidth);
        }
    }

    layout(width) {
        const { themes, maxColumnWidth = 250 } = this.props;

        if (!width || !themes.length) {
            return {};
        }

        const gap = 10;

        let columns = Math.max(3, Math.ceil(width / (maxColumnWidth)));

        if (app.isMobileOrTablet) {
            columns = 2;
        }

        let colWidth = (width - gap * (columns - 1)) / columns;
        let rowHeight = colWidth * 0.5625;

        if (!themes.at(0).get("isBuiltIn")) {
            rowHeight += 30;
        }

        let height = 0;
        let transforms = [];
        for (let i = 0; i < themes.length; i++) {
            let col = i % columns;
            let row = Math.floor(i / columns);

            let x = (colWidth + gap) * col;
            let y = (rowHeight + gap) * row;

            transforms.push({
                left: x,
                top: y,
                width: colWidth,
                height: rowHeight,
            });

            height = y + rowHeight + gap;
        }

        this.setState({ transforms, width, height });
    }

    getMenu = theme => {
        switch (this.props.type) {
            case ThemeType.USER:
                return (
                    <IconMenu>
                        <MenuItem onClick={() => this.handleEdit(theme)}><Icon>edit</Icon>{t("Edit Theme...")}</MenuItem>
                        <MenuItem onClick={() => this.handleRename(theme)}><Icon>label</Icon>{t("Rename")}</MenuItem>
                        <MenuItem key="delete"
                            onClick={() => this.handleDelete(theme)}><Icon>delete</Icon>{t("Delete...")}</MenuItem>
                    </IconMenu>
                );
        }
    }

    render() {
        const { themes, resources, selectedTheme, onClick, onDoubleClick, disabled } = this.props;
        const { transforms, height, transition } = this.state;

        return (
            <ThemeThumbnailGridContainer ref={this.ref} style={{ height, opacity: disabled ? 0.5 : 1 }}>
                {transforms && themes.map((theme, index) => {
                    return (
                        <ThemeThumbnailWrapper
                            id={`theme-thumbnail-${theme.id}`}
                            key={index}
                            bounds={transforms[index]}
                            onClick={() => !disabled && onClick(theme)}
                            onDoubleClick={() => !disabled && onDoubleClick(theme)}
                            transition={transition}
                        >
                            <ThemeThumbnail
                                item={theme}
                                resource={resources.find(r => r.resourceId === theme.id)}
                                selected={theme.id === selectedTheme?.id}
                                themeThumbnailGrid
                            />
                        </ThemeThumbnailWrapper>
                    );
                })}
            </ThemeThumbnailGridContainer>
        );
    }
}
